// src/pages/pos/POSStation.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api, { resolveMediaUrl } from '../../config/api';
import { print80mm } from '../../utils/printReceipt';
import useCurrency from '../../hooks/useCurrency';
import { useFeatures } from '../../hooks/useFeatures';
import useAuthStore from '../../store/authStore';
import ShiftWidget from '../../components/ShiftWidget';
import CalcWidget from '../../components/CalcWidget';
import ModifierPicker from '../../components/ModifierPicker';
import { posCache } from '../../utils/posCache';
import { enqueue } from '../../utils/offlineQueue';

const num = (obj, keys) => {
  if (!obj) return 0;
  for (const k of keys) { const v = obj[k]; if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) return Number(v); }
  return 0;
};
const str = (obj, keys) => {
  if (!obj) return '';
  for (const k of keys) { const v = obj[k]; if (v !== undefined && v !== null && v !== '') return String(v); }
  return '';
};

const CONFIG = {
  DOC_TYPE_SALES_OUT: 200,
  DEFAULT_RATE: 89500,
  endpoints: {
    settings: '/api/settings', categories: '/api/categories', products: '/api/products?all=true',
    paymentMethods: '/api/payment-methods', accounts: '/api/accounts',
    productByBarcode: (c) => `/api/products/barcode/${encodeURIComponent(c)}`,
    variantByBarcode: (c) => `/api/variants/barcode/${encodeURIComponent(c)}`,
    variants: (id) => `/api/products/${id}/variants`,
    documents: '/api/documents',
    docItems: (id) => `/api/documents/${id}/items`,
    docConfirm: (id) => `/api/documents/${id}/confirm`,
    docPayments: (id) => `/api/documents/${id}/payments`,
  },
  f: {
    id: (o) => o?._id ?? o?.id,
    settingsRate: (s) => num(s, ['exchange_rate']) || 89500,
    catId: (c) => c?._id ?? c?.id,
    catName: (c) => str(c, ['name', 'name_ar', 'title']),
    prodId: (p) => p?._id ?? p?.id,
    prodName: (p) => str(p, ['name', 'name_ar', 'title']),
    prodCatId: (p) => p?.category_id ?? p?.category?._id ?? null,
    prodStatus: (p) => str(p, ['status']) || 'active',
    prodType: (p) => str(p, ['type']) || 'simple',
    prodPriceUSD: (p) => num(p, ['price_usd']),
    prodPriceLBP: (p) => num(p, ['price_lbp']),
    prodCost: (p) => num(p, ['cost']),
    prodCostCur: (p) => str(p, ['cost_currency']) || 'USD',
    prodBarcode: (p) => str(p, ['barcode', 'sku']),
    prodHasVariants: (p) => (str(p, ['type']) === 'variable') || (Array.isArray(p?.variants) && p.variants.length > 0),
    prodStock: (p) => num(p, ['stock']),
    prodPriceLevels: (p) => p?.price_levels || {},
    prodUnit: (p) => str(p, ['unit']) || 'kg',
    prodPricePerUnit: (p) => num(p, ['price_per_unit']),
    varId: (v) => v?._id ?? v?.id,
    varProdId: (v) => v?.product_id ?? v?.productId,
    varName: (v) => str(v, ['name', 'title']),
    varPriceUSD: (v) => num(v, ['price_usd']),
    varPriceLBP: (v) => num(v, ['price_lbp']),
    varCost: (v) => num(v, ['cost']),
    varCostCur: (v) => str(v, ['cost_currency']) || 'USD',
    varStock: (v) => num(v, ['stock']),
    varBarcode: (v) => str(v, ['barcode', 'sku']),
    pmId: (m) => m?._id ?? m?.id,
    pmName: (m) => str(m, ['name', 'name_ar']),
    accId: (a) => a?._id ?? a?.id,
    accName: (a) => str(a, ['name']),
    accRoles: (a) => (Array.isArray(a?.roles) ? a.roles : []),
    accIsDefaultCash: (a) => Boolean(a?.is_default) && str(a, ['type']) === 'cash',
    accPriceLevelId: (a) => a?.price_level_id ?? null,
  },
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lbpFmt = (n) => Math.round(Number(n || 0)).toLocaleString('en-US');
const qtyFmt = (n) => { const v = Number(n) || 0; return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, ''); };
const todayLocal = () => { const d = new Date(); const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`; };
const costToUSD = (cost, cur, rate) => { const c = Number(cost)||0; if(!c) return 0; return cur==='LBP'?c/(rate||89500):c; };

const resolvePriceForAccount = (priceLevels, basePriceUSD, basePriceLBP, accountPriceLevelId, rate) => {
  if (accountPriceLevelId && priceLevels) {
    const lvl = priceLevels[accountPriceLevelId];
    if (lvl) {
      const usd = Number(lvl.usd)||0; const lbp = Number(lvl.lbp)||0;
      if (usd>0||lbp>0) { return { priceUSD: usd||(lbp?lbp/rate:0), priceLBP: lbp||(usd?Math.round(usd*rate):0) }; }
    }
  }
  const u = basePriceUSD||(basePriceLBP?basePriceLBP/rate:0);
  const l = basePriceLBP||(basePriceUSD?Math.round(basePriceUSD*rate):0);
  return { priceUSD: u, priceLBP: l };
};

let _posAudioCtx = null;
const beep = (kind='ok') => {
  try {
    const AC = window.AudioContext||window.webkitAudioContext; if(!AC) return;
    _posAudioCtx = _posAudioCtx||new AC();
    const ctx = _posAudioCtx; if(ctx.state==='suspended') ctx.resume();
    const osc=ctx.createOscillator(); const gain=ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    const dur=kind==='error'?0.32:0.10; osc.type=kind==='error'?'square':'sine'; osc.frequency.value=kind==='error'?200:920;
    gain.gain.setValueAtTime(0.0001,ctx.currentTime); gain.gain.exponentialRampToValueAtTime(0.22,ctx.currentTime+0.01); gain.gain.exponentialRampToValueAtTime(0.0001,ctx.currentTime+dur);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime+dur);
  } catch {}
};

const GREEN = '#2e9f86';
const STORAGE_PREFIX = 'mmm_pos_tabs_v2';
// tabs are stored PER TENANT — otherwise an unsaved cart from one shop leaks
// into another account on the same browser (old v1 key is cleaned up below)
const tenantFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.tenant || '';
  } catch { return ''; }
};
const makeTab = () => ({ id:`t${Date.now()}${Math.random().toString(36).slice(2,5)}`, cart:[], selectedKey:null, payMode:'cash', accountId:'', cashCustomer:'', date:todayLocal(), includeTax:false });
const loadState = (key) => {
  try { localStorage.removeItem('mmm_pos_tabs_v1'); } catch {} // legacy un-namespaced key
  try { const raw=localStorage.getItem(key); if(raw){const o=JSON.parse(raw);if(o?.tabs?.length){const tabs=o.tabs.map(tb=>({...tb,date:todayLocal()}));const activeId=tabs.find(t=>t.id===o.activeId)?o.activeId:tabs[0].id;return{tabs,activeId};}}} catch {}
  const t=makeTab(); return {tabs:[t],activeId:t.id};
};
const makeDelivery = () => ({ address:'', phone:'', driverUserId:'', fee:'', notes:'', paidNow:false });

// ── detect mobile ─────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
};

export default function POSStation() {
  const { t } = useTranslation();
  const features = useFeatures();
  const isMobile = useIsMobile();

  // initialDataUpdatedAt: 0 → the localStorage cache renders instantly but is treated as
  // STALE, so settings (exchange rate!) always refetch on mount. If the API is unreachable
  // the cached copy stays on screen — offline behaviour is unchanged.
  const { data: settings } = useQuery(['settings'], () => api.get(CONFIG.endpoints.settings).then(r=>r.data), { initialData: posCache.settings.initialData, initialDataUpdatedAt: 0, onSuccess: posCache.settings.save, staleTime: 5*60*1000 });
  const rate = Number(CONFIG.f.settingsRate(settings))||89500;
  const { secCode } = useCurrency();
  const taxFeatureEnabled = !!settings?.features?.tax;
  const defaultTaxRate    = Number(settings?.features?.default_tax_rate) || 0;
  // display currency: settings default + in-POS quick switch (persisted per browser)
  const settingsShowLBP = settings?.currency?.show_lbp ?? true;
  const [currOverride, setCurrOverride] = useState(() => { try { return localStorage.getItem('mmm_pos_curr') || ''; } catch { return ''; } });
  const [cardSize, setCardSize] = useState(() => { try { return Number(localStorage.getItem('mmm_pos_card_size')) || 120; } catch { return 120; } });
  const changeCardSize = (delta) => { const next = Math.min(280, Math.max(40, cardSize + delta)); setCardSize(next); try { localStorage.setItem('mmm_pos_card_size', next); } catch {} };
  // phones: switch between the computer layout (zoomed out) and the phone layout — see public/index.html
  const isTouch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const desktopOnPhone = typeof document !== 'undefined' && document.documentElement.getAttribute('data-desktop-on-phone') === '1';
  const switchView = () => { try { localStorage.setItem('mmm_view_mode', desktopOnPhone ? 'mobile' : 'desktop'); } catch {} window.location.reload(); };
  const showLBP = settingsShowLBP && (currOverride ? currOverride === 'lbp' : true);
  const toggleCurrency = () => {
    const next = showLBP ? 'usd' : 'lbp';
    setCurrOverride(next);
    try { localStorage.setItem('mmm_pos_curr', next); } catch {}
  };
  // scale barcode config: { enabled, prefix: "20", mode: "weight"|"price" }
  const scaleCfg = settings?.scale || {};

  const { data: categories=[] }    = useQuery(['categories'],      ()=>api.get(CONFIG.endpoints.categories).then(r=>asArray(r.data)),      { initialData: posCache.categories.initialData, initialDataUpdatedAt: 0,     onSuccess: posCache.categories.save,     staleTime: 5*60*1000 });
  const { data: paymentMethods=[] }= useQuery(['payment-methods'], ()=>api.get(CONFIG.endpoints.paymentMethods).then(r=>asArray(r.data)), { initialData: posCache.paymentMethods.initialData, initialDataUpdatedAt: 0, onSuccess: posCache.paymentMethods.save, staleTime: 5*60*1000 });
  const { data: accounts=[] }      = useQuery(['accounts'],        ()=>api.get(CONFIG.endpoints.accounts).then(r=>asArray(r.data)),       { initialData: posCache.accounts.initialData, initialDataUpdatedAt: 0,       onSuccess: posCache.accounts.save,       staleTime: 5*60*1000 });
  const { data: allProducts=[] }   = useQuery(['products-pos'],    ()=>api.get(CONFIG.endpoints.products).then(r=>asArray(r.data)),       { initialData: posCache.products.initialData, initialDataUpdatedAt: 0,       onSuccess: posCache.products.save,       staleTime: 5*60*1000 });
  const { data: drivers=[] } = useQuery(['users-drivers'], ()=>api.get('/api/users/drivers').then(r=>asArray(r.data)), { enabled:!!features.delivery });

  const token = useAuthStore((s)=>s.token);
  const storageKey = useMemo(()=>`${STORAGE_PREFIX}_${tenantFromToken(token)}`,[token]);
  const initial = useMemo(()=>loadState(storageKey), [storageKey]);
  const [tabs, setTabs] = useState(initial.tabs);
  const [activeId, setActiveId] = useState(initial.activeId);
  const active = tabs.find(tb=>tb.id===activeId)||tabs[0];

  useEffect(()=>{ try{localStorage.setItem(storageKey,JSON.stringify({tabs,activeId}));}catch{} },[tabs,activeId,storageKey]);
  useEffect(()=>{ const h=(e)=>{if(tabs.some(tb=>tb.cart.length)){e.preventDefault();e.returnValue='';}}; window.addEventListener('beforeunload',h); return()=>window.removeEventListener('beforeunload',h); },[tabs]);

  const cart        = useMemo(()=>active?.cart||[],[active]);
  const selectedKey = active?.selectedKey??null;
  const payMode     = features.credit_sales !== false ? (active?.payMode||'cash') : 'cash';
  const accountId   = active?.accountId||'';
  const cashCustomer= active?.cashCustomer||'';
  const date        = active?.date||todayLocal();
  const includeTax  = active?.includeTax||false;

  const patchActive = (patch) => setTabs(ts=>ts.map(tb=>tb.id===activeId?{...tb,...(typeof patch==='function'?patch(tb):patch)}:tb));
  const setSelectedKey  = (v) => patchActive({selectedKey:v});
  const setPayMode      = (v) => patchActive({payMode:v});
  const setAccountId    = (v) => patchActive({accountId:v});
  const setCashCustomer = (v) => patchActive({cashCustomer:v});
  const setDate         = (v) => patchActive({date:v});
  const setIncludeTax   = (v) => patchActive({includeTax:v});

  const [activeCat,     setActiveCat]     = useState(null);
  const [search,        setSearch]        = useState('');
  const [scanMsg,       setScanMsg]       = useState(null);
  const [methodId,      setMethodId]      = useState('');
  const [variantModal,  setVariantModal]  = useState(null);
  const [modFor,        setModFor]        = useState(null);
  const [quickCreate,   setQuickCreate]   = useState(null); // {barcode} — scan-to-create
  const [payModal,      setPayModal]      = useState(null);
  const queryClient = useQueryClient();
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [delivery,      setDelivery]      = useState(makeDelivery());
  const [discountInput, setDiscountInput] = useState({type:'pct',value:''});
  const [busy,          setBusy]          = useState(false);
  const [mobileTab,     setMobileTab]     = useState('products'); // 'products' | 'cart'

  const barcodeRef = useRef(null);
  const rootRef    = useRef(null);
  const scanTimer  = useRef(null);
  const [areaH, setAreaH] = useState('100vh');

  const flashScan = useCallback((text,kind='error')=>{ beep(kind); setScanMsg({text,kind}); clearTimeout(scanTimer.current); scanTimer.current=setTimeout(()=>setScanMsg(null),2400); },[]);
  useEffect(()=>()=>clearTimeout(scanTimer.current),[]);

  useEffect(()=>{
    const fit=()=>{
      const el=rootRef.current; if(!el) return;
      let h=0; const parent=el.parentElement;
      if(parent){const cs=getComputedStyle(parent);const padY=(parseFloat(cs.paddingTop)||0)+(parseFloat(cs.paddingBottom)||0);const ph=parent.clientHeight-padY;if(ph>200)h=ph;}
      // cap by the REAL visible viewport (visualViewport tracks mobile URL bar + keyboard)
      const top=el.getBoundingClientRect().top;
      const vvH=window.visualViewport?.height||window.innerHeight;
      const visible=vvH-top-1;
      h=h?Math.min(h,visible):visible;
      setAreaH(`${Math.max(240,Math.floor(h))}px`);
    };
    fit(); const id=setTimeout(fit,120);
    const prevHtml=document.documentElement.style.overflow; const prevBody=document.body.style.overflow;
    document.documentElement.style.overflow='hidden'; document.body.style.overflow='hidden';
    window.addEventListener('resize',fit); document.addEventListener('fullscreenchange',fit);
    window.visualViewport?.addEventListener('resize',fit);
    window.addEventListener('orientationchange',fit);
    return()=>{ clearTimeout(id); window.removeEventListener('resize',fit); document.removeEventListener('fullscreenchange',fit); window.visualViewport?.removeEventListener('resize',fit); window.removeEventListener('orientationchange',fit); document.documentElement.style.overflow=prevHtml; document.body.style.overflow=prevBody; };
  },[]);

  useEffect(()=>{
    if(!paymentMethods.length) return;
    const isValid = paymentMethods.some(m=>String(CONFIG.f.pmId(m))===methodId);
    if(!methodId||!isValid){ const def=paymentMethods.find(m=>m.is_default)||paymentMethods[0]; setMethodId(String(CONFIG.f.pmId(def))); }
  },[paymentMethods,methodId]);

  // fullscreen is now manual — button in POS header + sidebar

  const products = useMemo(()=>allProducts.filter(p=>CONFIG.f.prodStatus(p)!=='inactive'),[allProducts]);
  const customerAccounts = useMemo(()=>accounts.filter(a=>{const r=CONFIG.f.accRoles(a);return r.length===0||r.includes('customer')||r.includes('both');}),[accounts]);
  const selectedAccount = useMemo(()=>accounts.find(a=>String(CONFIG.f.accId(a))===String(accountId)),[accounts,accountId]);
  const accountPriceLevelId = selectedAccount?CONFIG.f.accPriceLevelId(selectedAccount):null;

  useEffect(()=>{
    if(activeCat!=null || !categories.length) return;        // wait for categories, then open the default one
    const def = categories.find(c=>c.default_pos);
    setActiveCat(def ? String(CONFIG.f.catId(def)) : 'all');
  },[activeCat, categories]);
  useEffect(()=>{ if(!isMobile) barcodeRef.current?.focus(); },[isMobile]);

  const knownCatIds = useMemo(()=>new Set(categories.map(c=>String(CONFIG.f.catId(c)))),[categories]);
  const hasUncategorized = useMemo(()=>products.some(p=>{const cid=CONFIG.f.prodCatId(p);return !cid||!knownCatIds.has(String(cid));}),[products,knownCatIds]);

  const GRID_LIMIT = 50;
  const visibleProducts = useMemo(()=>{
    let list=products.filter(p=>p.sellable!==false); // raw materials hidden from POS
    // hide variable products if variants feature is off
    if(features.variants===false) list=list.filter(p=>CONFIG.f.prodType(p)!=='variable');
    if(search.trim()){const q=search.trim().toLowerCase();list=list.filter(p=>CONFIG.f.prodName(p)?.toLowerCase().includes(q));}
    else if(activeCat==='other'){list=list.filter(p=>{const cid=CONFIG.f.prodCatId(p);return !cid||!knownCatIds.has(String(cid));});}
    else if(activeCat!=null&&activeCat!=='all'){list=list.filter(p=>String(CONFIG.f.prodCatId(p))===String(activeCat));}
    return list.slice(0, GRID_LIMIT);
  },[products,search,activeCat,knownCatIds,features.variants]);

  const addRow = useCallback((row)=>{
    setTabs(ts=>ts.map(tb=>{
      if(tb.id!==activeId)return tb;
      const hasMods=(row.modifiers&&row.modifiers.length)||row.note;
      const idx=hasMods?-1:tb.cart.findIndex(r=>r.prodId===row.prodId&&r.variantId===row.variantId&&!r.scaled&&!(r.modifiers&&r.modifiers.length)&&!r.note);
      if(idx>=0){const c=[...tb.cart];c[idx]={...c[idx],qty:c[idx].qty+1};return{...tb,cart:c,selectedKey:c[idx].key};}
      const key=`${row.prodId}:${row.variantId??'-'}:${Date.now()}`;
      return{...tb,cart:[{...row,key,qty:1},...tb.cart],selectedKey:key};
    }));
    if(isMobile) setMobileTab('cart');
  },[activeId,isMobile]);

  // scaled items: decimal qty (kg), merged by adding weights
  const addScaledRow = useCallback((p, qty, priceUSD)=>{
    const w = Math.max(0.001, Number(qty)||0);
    setTabs(ts=>ts.map(tb=>{
      if(tb.id!==activeId)return tb;
      const prodId=CONFIG.f.prodId(p);
      const idx=tb.cart.findIndex(r=>r.prodId===prodId&&r.scaled);
      if(idx>=0){const c=[...tb.cart];c[idx]={...c[idx],qty:Number((c[idx].qty+w).toFixed(3))};return{...tb,cart:c,selectedKey:c[idx].key};}
      const key=`${prodId}:scaled:${Date.now()}`;
      const row={
        prodId, variantId:null, scaled:true,
        name:`${CONFIG.f.prodName(p)} (${CONFIG.f.prodUnit(p)})`,
        unit:CONFIG.f.prodUnit(p),
        priceUSD, priceLBP:Math.round(priceUSD*rate),
        costUSD:costToUSD(CONFIG.f.prodCost(p),CONFIG.f.prodCostCur(p),rate),
        key, qty:Number(w.toFixed(3)),
      };
      return{...tb,cart:[row,...tb.cart],selectedKey:key};
    }));
    if(isMobile) setMobileTab('cart');
  },[activeId,isMobile,rate]);

  const addProduct = useCallback((p)=>{
    const pl=CONFIG.f.prodPriceLevels(p);
    const{priceUSD,priceLBP}=resolvePriceForAccount(pl,CONFIG.f.prodPriceUSD(p),CONFIG.f.prodPriceLBP(p),accountPriceLevelId,rate);
    addRow({prodId:CONFIG.f.prodId(p),variantId:null,name:CONFIG.f.prodName(p),priceUSD,priceLBP,costUSD:costToUSD(CONFIG.f.prodCost(p),CONFIG.f.prodCostCur(p),rate)});
  },[addRow,rate,accountPriceLevelId]);

  const addProductWithMods = useCallback((p,sel)=>{
    const mods=(sel&&sel.modifiers)||[]; const note=(sel&&sel.note)||'';
    const pl=CONFIG.f.prodPriceLevels(p);
    const{priceUSD:basePrice}=resolvePriceForAccount(pl,CONFIG.f.prodPriceUSD(p),CONFIG.f.prodPriceLBP(p),accountPriceLevelId,rate);
    // extra is the sum of modifier prices (USD); guard against NaN/negative
    const extra=Math.max(0,mods.reduce((s,m)=>s+Number(m.price)||s,0));
    const finalUSD=basePrice+extra;
    addRow({prodId:CONFIG.f.prodId(p),variantId:null,name:CONFIG.f.prodName(p),priceUSD:finalUSD,priceLBP:Math.round(finalUSD*rate),costUSD:costToUSD(CONFIG.f.prodCost(p),CONFIG.f.prodCostCur(p),rate),modifiers:mods,note});
  },[addRow,rate,accountPriceLevelId]);

  const addVariant = useCallback((p,v)=>{
    const pl=CONFIG.f.prodPriceLevels(p);
    const{priceUSD,priceLBP}=resolvePriceForAccount(pl,CONFIG.f.varPriceUSD(v)||CONFIG.f.prodPriceUSD(p),CONFIG.f.varPriceLBP(v)||CONFIG.f.prodPriceLBP(p),accountPriceLevelId,rate);
    const rawCost=CONFIG.f.varCost(v)||CONFIG.f.prodCost(p); const rawCur=CONFIG.f.varCost(v)?CONFIG.f.varCostCur(v):CONFIG.f.prodCostCur(p);
    addRow({prodId:CONFIG.f.prodId(p),variantId:CONFIG.f.varId(v),name:`${CONFIG.f.prodName(p)} - ${CONFIG.f.varName(v)}`,variantName:CONFIG.f.varName(v),priceUSD,priceLBP,costUSD:costToUSD(rawCost,rawCur,rate)});
  },[addRow,rate,accountPriceLevelId]);

  const handleProductClick = useCallback(async(p)=>{
    // scaled product tapped manually -> ask for the weight
    if(CONFIG.f.prodType(p)==='scaled'){
      const unit=CONFIG.f.prodUnit(p);
      const w=parseFloat(window.prompt(`${CONFIG.f.prodName(p)} — ${t('pos.enterWeight','Enter weight')} (${unit}):`,''));
      if(!w||w<=0) return;
      const ppu=CONFIG.f.prodPricePerUnit(p)||CONFIG.f.prodPriceUSD(p);
      addScaledRow(p,w,ppu);
      return;
    }
    if(CONFIG.f.prodHasVariants(p)){
      try{const variants=await api.get(CONFIG.endpoints.variants(CONFIG.f.prodId(p))).then(r=>asArray(r.data));if(variants.length){setVariantModal({product:p,variants});return;}}
      catch(err){console.warn('variant fetch failed',err);}
    }
    if((p.modifier_group_ids||[]).length){ setModFor(p); return; }
    addProduct(p);
  },[addProduct,addScaledRow,t]);

  const pickVariant=(v)=>{addVariant(variantModal.product,v);setVariantModal(null);if(!isMobile)barcodeRef.current?.focus();};

  // ── scale barcode: PREFIX(2) + PLU(5) + VALUE(5) + CHECK(1) = 13 digits ──
  const tryScaleBarcode = (code) => {
    if(!scaleCfg.enabled) return false;
    const prefix=String(scaleCfg.prefix||'20');
    if(!/^\d{13}$/.test(code)||!code.startsWith(prefix)) return false;
    const plu=code.slice(2,7);
    const value=Number(code.slice(7,12));
    const pluNum=String(Number(plu)); // "00123" -> "123" for tolerant matching
    const p=products.find(x=>{
      if(CONFIG.f.prodType(x)!=='scaled') return false;
      const b=String(CONFIG.f.prodBarcode(x)).trim();
      return b===plu||String(Number(b))===pluNum;
    });
    if(!p){ flashScan(t('pos.noPlu',`No scale product for PLU "${plu}"`)); return true; }
    const ppu=CONFIG.f.prodPricePerUnit(p)||CONFIG.f.prodPriceUSD(p);
    if((scaleCfg.mode||'weight')==='price'){
      const price=value/100; // 5 digits, 2 decimals
      if(ppu>0){ addScaledRow(p,Number((price/ppu).toFixed(3)),ppu); }
      else { addScaledRow(p,1,price); } // no unit price set -> charge label price as 1 unit
    } else {
      const kg=value/1000; // grams -> kg
      addScaledRow(p,kg,ppu);
    }
    beep('ok');
    return true;
  };

  const onBarcodeKey=async(e)=>{
    if(e.key!=='Enter')return;
    const code=String(e.currentTarget.value||'').trim(); if(!code)return;
    const clear=()=>{if(barcodeRef.current)barcodeRef.current.value='';setSearch('');};
    if(tryScaleBarcode(code)){clear();return;}
    let p=products.find(x=>String(CONFIG.f.prodBarcode(x))===code);
    if(p){if(!CONFIG.f.prodHasVariants(p)&&CONFIG.f.prodType(p)!=='scaled')beep('ok');await handleProductClick(p);clear();return;}
    try{const res=await api.get(CONFIG.endpoints.productByBarcode(code)).then(r=>r.data);const found=Array.isArray(res)?res[0]:(res?.data??res);if(found&&CONFIG.f.prodId(found)){if(!CONFIG.f.prodHasVariants(found)&&CONFIG.f.prodType(found)!=='scaled')beep('ok');await handleProductClick(found);clear();return;}}catch{}
    try{const res=await api.get(CONFIG.endpoints.variantByBarcode(code)).then(r=>r.data);const v=Array.isArray(res)?res[0]:(res?.data??res);if(v&&CONFIG.f.varId(v)){const parent=products.find(x=>String(CONFIG.f.prodId(x))===String(CONFIG.f.varProdId(v)))||{_id:CONFIG.f.varProdId(v),name:v.product_name||''};addVariant(parent,v);beep('ok');clear();return;}}catch{}
    // unknown barcode → offer to create it on the spot (catalog builds itself while selling)
    beep('error');
    setQuickCreate({ barcode: code });
    clear();
  };

  const handleQuickCreated = (p) => {
    setQuickCreate(null);
    queryClient.invalidateQueries(['products-pos']);
    beep('ok');
    addProduct(p);
    if(!isMobile) barcodeRef.current?.focus();
  };

  const setQty=(key,qty)=>setTabs(ts=>ts.map(tb=>tb.id===activeId?{...tb,cart:tb.cart.map(r=>{
    if(r.key!==key)return r;
    const min=r.scaled?0.001:1;
    const q=Math.max(min,Number(qty)||min);
    return{...r,qty:r.scaled?Number(q.toFixed(3)):Math.round(q)};
  })}:tb));
  const setRowPrice=(key,usd)=>{const p=Math.max(0,Number(usd)||0);setTabs(ts=>ts.map(tb=>tb.id===activeId?{...tb,cart:tb.cart.map(r=>r.key===key?{...r,priceUSD:p,priceLBP:Math.round(p*rate)}:r)}:tb));};
  const removeRow=(key)=>setTabs(ts=>ts.map(tb=>tb.id===activeId?{...tb,cart:tb.cart.filter(r=>r.key!==key)}:tb));
  const applyKeypad=(n)=>{if(selectedKey)setQty(selectedKey,n);};
  const toggleFullscreen=()=>{if(!document.fullscreenElement)document.documentElement.requestFullscreen?.().catch(()=>{});else document.exitFullscreen?.();};
  const newTab=()=>{const nt=makeTab();setTabs(ts=>[...ts,nt]);setActiveId(nt.id);if(!isMobile)barcodeRef.current?.focus();};
  const closeTab=(idToClose)=>{
    const tab=tabs.find(x=>x.id===idToClose);
    if(tab&&tab.cart.length&&!window.confirm(t('pos.closeBill','Close this bill?')))return;
    let left=tabs.filter(x=>x.id!==idToClose);if(left.length===0)left=[makeTab()];
    setTabs(left);if(idToClose===activeId)setActiveId(left[0].id);
  };

  const totalUSD  = useMemo(()=>cart.reduce((s,r)=>s+r.priceUSD*r.qty,0),[cart]);
  const totalLBP  = useMemo(()=>cart.reduce((s,r)=>s+r.priceLBP*r.qty,0),[cart]);
  const itemCount = cart.length;
  const qtyCount  = useMemo(()=>cart.reduce((s,r)=>s+(r.scaled?1:r.qty),0),[cart]);
  const discountAmt = useMemo(()=>{
    const v=Number(discountInput.value)||0;
    if(!v||!cart.length) return 0;
    return discountInput.type==='pct' ? Math.min(totalUSD*v/100, totalUSD) : Math.min(v, totalUSD);
  },[discountInput,totalUSD,cart.length]);
  const finalTotalUSD  = totalUSD - discountAmt;
  const deliveryFeeUSD = Number(delivery.fee)||0;
  const grandTotalUSD  = finalTotalUSD+deliveryFeeUSD;
  const grandTotalLBP  = Math.round(grandTotalUSD*rate); // derived from USD — prevents negative values & stays consistent
  // VAT extracted from grand total (inclusive): total × rate / (1 + rate)
  const vatUSD = includeTax && defaultTaxRate > 0 ? grandTotalUSD * (defaultTaxRate/100) / (1 + defaultTaxRate/100) : 0;

  const openDeliveryModal=()=>{if(!cart.length||busy)return;setDeliveryModal(true);};
  // 1-click cash tender: full amount in the currency the POS is currently showing
  // (USD, or the second currency when the LBP view is active). No popup for cash.
  const cashTender=(usd)=> features.second_currency
    ? { payCurrency: showLBP ? secCode : 'USD', payAmount: showLBP ? Math.round(usd*rate) : usd }
    : {};
  const startCheckout=(print)=>{
    if(!cart.length||busy)return;
    if(payMode==='credit'){if(!accountId){alert(t('pos.pickAccount','Please select a customer account'));return;}setPayModal({print});}
    else doSale({print,paidUSD:finalTotalUSD,deliveryData:null,...cashTender(finalTotalUSD)});
  };
  const confirmDelivery=(print)=>{
    if(!delivery.address.trim()){alert('Please enter a delivery address');return;}
    setDeliveryModal(null);
    if(payMode==='credit'){if(!accountId){alert(t('pos.pickAccount','Please select a customer account'));return;}setPayModal({print,isDelivery:true});}
    // COD: leave balance open — the driver collects the cash, settle later in Driver Summary.
    // If the customer prepaid, the "Paid in advance" checkbox records full payment now.
    else doSale({print,paidUSD:delivery.paidNow?grandTotalUSD:0,deliveryData:delivery,...(delivery.paidNow?cashTender(grandTotalUSD):{})});
  };

  const doSale=async({print,paidUSD,deliveryData,payAmount,payCurrency})=>{
    setBusy(true);
    // Build payload before any await so we can queue it on network failure
    const cashAcc=accounts.find(a=>CONFIG.f.accIsDefaultCash(a));
    const resolvedAccountId=payMode==='credit'?accountId:(cashAcc?CONFIG.f.accId(cashAcc):null);
    const docNotes=payMode==='cash'&&cashCustomer?`Customer: ${cashCustomer}`:undefined;
    const docDelivery=deliveryData?{enabled:true,address:deliveryData.address,phone:deliveryData.phone,driver_id:deliveryData.driverUserId||null,fee:Number(deliveryData.fee)||0,notes:deliveryData.notes,status:'pending'}:undefined;
    const docBody={type:CONFIG.DOC_TYPE_SALES_OUT,account_id:resolvedAccountId,date,currency:'USD',notes:docNotes,source:'pos',...(docDelivery?{delivery:docDelivery}:{}),...(discountAmt>0?{discount_amount:discountAmt}:{})};
    const calcItemTax=(r)=>includeTax&&defaultTaxRate>0 ? r.priceUSD*r.qty*(defaultTaxRate/100)/(1+defaultTaxRate/100) : 0;
    const queuePayload={account_id:resolvedAccountId,date,notes:docNotes,source:'pos',delivery:docDelivery,discount_amount:discountAmt||0,items:cart.map(r=>({product_id:r.prodId,variant_id:r.variantId||undefined,product_name:r.name+((r.modifiers&&r.modifiers.length)?` (${r.modifiers.map(m=>m.name).join(', ')})`:''),variant_name:r.variantName||undefined,quantity:r.qty,price:r.priceUSD,price_currency:'USD',cost:r.costUSD||0,cost_currency:'USD',tax:calcItemTax(r),discount:0})),paidUSD,methodId,pay_currency:payCurrency||'USD',pay_amount:(payCurrency&&payCurrency!=='USD')?(payAmount||Math.round(paidUSD*rate)):paidUSD};
    try{
      const doc=await api.post(CONFIG.endpoints.documents,docBody).then(r=>r.data?.data??r.data);
      const docId=CONFIG.f.id(doc); if(!docId)throw new Error('Document was not created');
      // Try bulk endpoint first (fast — 1 call); fall back to individual calls if not yet deployed
      try{
        await api.post(`${CONFIG.endpoints.docItems(docId)}/bulk`,{items:cart.map(r=>({product_id:r.prodId,variant_id:r.variantId||undefined,product_name:r.name+((r.modifiers&&r.modifiers.length)?` (${r.modifiers.map(m=>m.name).join(', ')})`:''),variant_name:r.variantName||undefined,quantity:r.qty,price:r.priceUSD,price_currency:'USD',cost:r.costUSD||0,cost_currency:'USD',tax:calcItemTax(r),discount:0}))});
      }catch(bulkErr){
        if(bulkErr?.response?.status===404){
          for(const r of cart){await api.post(CONFIG.endpoints.docItems(docId),{product_id:r.prodId,variant_id:r.variantId||undefined,product_name:r.name+((r.modifiers&&r.modifiers.length)?` (${r.modifiers.map(m=>m.name).join(', ')})`:''),variant_name:r.variantName||undefined,quantity:r.qty,price:r.priceUSD,price_currency:'USD',cost:r.costUSD||0,cost_currency:'USD',tax:calcItemTax(r),discount:0});}
        }else{throw bulkErr;}
      }
      try { await api.post(CONFIG.endpoints.docConfirm(docId),{}); }
      catch (ce) {
        // over the customer's credit limit — the owner / a manager may sell anyway (kept in the audit trail)
        const d = ce?.response?.data;
        const role = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return ''; } })();
        if (d?.reason === 'credit_limit' && ['admin', 'manager'].includes(role) && window.confirm(`${d.error}\n\nSell anyway?`)) await api.post(CONFIG.endpoints.docConfirm(docId), { override_credit_limit: true });
        else throw ce;
      }
      if(paidUSD>0&&methodId){const _cur=payCurrency||'USD';const _amt=_cur==='USD'?paidUSD:(payAmount||Math.round(paidUSD*rate));await api.post(CONFIG.endpoints.docPayments(docId),{payment_method_id:methodId,amount:_amt,currency:_cur});}
      if(print)print80mm({company:settings?.company||{},items:[...cart.map(r=>({name:r.name,variantName:r.variantName||'',qty:r.qty,priceUSD:r.priceUSD,priceLBP:r.priceLBP})),...(deliveryData&&deliveryFeeUSD>0?[{name:'Delivery Fee',qty:1,priceUSD:deliveryFeeUSD,priceLBP:Math.round(deliveryFeeUSD*rate)}]:[])],totals:{total:grandTotalUSD,totalLBP:grandTotalLBP,discount:discountAmt,vat:vatUSD,vatRate:defaultTaxRate},paidUSD,showLBP,date,docNumber:doc?.number||doc?._id||'',rate,...(deliveryData?{deliveryAddress:deliveryData.address,deliveryPhone:deliveryData.phone}:{})});
      resetSale();
    }catch(err){
      const isNetErr=!navigator.onLine||err?.code==='ERR_NETWORK'||err?.message==='Network Error';
      if(isNetErr){
        enqueue(queuePayload);
        resetSale();
        flashScan('محفوظة — ستُرسل تلقائياً / Saved — will sync when online','ok');
      }else{
        alert((err?.response?.data?.message||err?.response?.data?.error||err.message||'Error')+'');
      }
    }
    finally{setBusy(false);setPayModal(null);}
  };

  const resetSale=()=>{patchActive({cart:[],selectedKey:null,cashCustomer:'',accountId:'',payMode:'cash',includeTax:false});setDelivery(makeDelivery());setDiscountInput({type:'pct',value:''});if(!isMobile)barcodeRef.current?.focus();};

  useEffect(()=>{
    const onKey=(e)=>{
      if(e.key==='F4'){e.preventDefault();startCheckout(false);}
      else if(e.key==='F3'){e.preventDefault();startCheckout(true);}
      else if(e.key==='F1'){e.preventDefault();barcodeRef.current?.focus();}
      else if(e.key==='F2'){e.preventDefault();newTab();}
    };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  }); // eslint-disable-line

  // ── product grid (shared) ─────────────────────────────────────────────────
  const ProductGrid = (
    <div className="flex flex-col flex-1 min-h-0">
      {/* search on mobile */}
      {isMobile && (
        <div className="px-3 pt-2 pb-1 bg-white border-b">
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('pos.search','Search product…')}
                 className="w-full border rounded px-3 py-2 text-sm" style={{borderColor:GREEN}} />
        </div>
      )}
      {/* categories */}
      <div className="flex gap-2 p-2 border-b bg-white overflow-x-auto shrink-0" style={{flexWrap:'nowrap'}}>
        {(()=>{const on=activeCat==='all'&&!search;return(
          <button onClick={()=>{setActiveCat('all');setSearch('');}} className={`px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap ${on?'text-white':'bg-gray-100 text-gray-700'}`} style={on?{backgroundColor:GREEN,borderColor:GREEN}:{}}>{t('pos.allCats','All')}</button>
        );})()}
        {categories.map(c=>{
          const id=CONFIG.f.catId(c); const on=String(id)===String(activeCat)&&!search;
          return <button key={id} onClick={()=>{setActiveCat(id);setSearch('');}} className={`px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap ${on?'text-white':'bg-gray-100 text-gray-700'}`} style={on?{backgroundColor:GREEN,borderColor:GREEN}:{}}>{CONFIG.f.catName(c)}</button>;
        })}
        {hasUncategorized&&(()=>{const on=activeCat==='other'&&!search;return(
          <button onClick={()=>{setActiveCat('other');setSearch('');}} className={`px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap ${on?'text-white':'bg-gray-100 text-gray-700'}`} style={on?{backgroundColor:GREEN,borderColor:GREEN}:{}}>{t('pos.otherCats','Other')}</button>
        );})()}
      </div>
      {/* grid */}
      <div className="flex-1 overflow-y-auto p-3">
        {(!isMobile || isTouch) && (
          <div style={{display:'flex',alignItems:'center',gap:desktopOnPhone?10:6,marginBottom:8,justifyContent:'flex-end'}}>
            {isTouch && (
              <button onClick={switchView} title={desktopOnPhone?'Phone layout':'Computer layout'}
                style={{height:desktopOnPhone?48:28,padding:'0 12px',borderRadius:8,border:'1.5px solid #e5e7eb',background:'#fff',fontSize:desktopOnPhone?18:13,fontWeight:700,color:'#374151'}}>
                {desktopOnPhone?'📱 Phone view':'🖥 Computer view'}
              </button>
            )}
            {!isMobile && (<>
            <button onClick={()=>changeCardSize(-20)} disabled={cardSize<=40}
              style={{width:desktopOnPhone?48:28,height:desktopOnPhone?48:28,borderRadius:desktopOnPhone?10:6,border:'1.5px solid #e5e7eb',background:'#fff',fontSize:desktopOnPhone?26:16,fontWeight:700,cursor:cardSize<=40?'not-allowed':'pointer',color:cardSize<=40?'#d1d5db':'#374151',lineHeight:1}}>−</button>
            <button onClick={()=>changeCardSize(20)} disabled={cardSize>=280}
              style={{width:desktopOnPhone?48:28,height:desktopOnPhone?48:28,borderRadius:desktopOnPhone?10:6,border:'1.5px solid #e5e7eb',background:'#fff',fontSize:desktopOnPhone?26:16,fontWeight:700,cursor:cardSize>=280?'not-allowed':'pointer',color:cardSize>=280?'#d1d5db':'#374151',lineHeight:1}}>+</button>
            </>)}
          </div>
        )}
        <div className="grid gap-2" style={{gridTemplateColumns:`repeat(auto-fill, minmax(${isMobile?'100px':cardSize+'px'}, 1fr))`}}>
          {visibleProducts.map(p=>{
            const imgSrc=resolveMediaUrl(p.image_url);
            const cardH = isMobile ? 80 : cardSize;
            return (
              <button key={CONFIG.f.prodId(p)} onClick={()=>handleProductClick(p)}
                      className="rounded-lg font-bold shadow-sm border hover:opacity-90 relative overflow-hidden"
                      style={{backgroundColor:GREEN,borderColor:'#c8b4ff',padding:0,height:cardH,fontSize:Math.max(9,Math.min(22,cardSize/10))}}>
                {imgSrc?(
                  <>{<img src={imgSrc} alt={CONFIG.f.prodName(p)} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />}
                  <div style={{position:'absolute',bottom:0,left:0,right:0,background:'rgba(0,0,0,0.45)',color:'#fff',fontSize:Math.max(8,Math.min(18,cardSize/12)),fontWeight:700,padding:'3px 4px',textAlign:'center',lineHeight:1.2}}>{CONFIG.f.prodName(p)}</div></>
                ):(
                  <div className="flex items-center justify-center text-center px-1 w-full h-full text-white" style={{fontSize:Math.max(9,Math.min(22,cardSize/10))}}>{CONFIG.f.prodName(p)}</div>
                )}
                {CONFIG.f.prodHasVariants(p)&&<span className="absolute top-1 end-1 text-[10px] bg-white/80 rounded px-1">▾</span>}
                {CONFIG.f.prodType(p)==='scaled'&&<span className="absolute top-1 start-1 text-[10px] bg-white/80 rounded px-1">⚖️</span>}
              </button>
            );
          })}
          {!visibleProducts.length&&<div className="col-span-full text-center text-gray-400 py-8 text-sm">{t('pos.noProducts','No products')}</div>}
        </div>
      </div>
    </div>
  );

  // ── cart table (shared) ───────────────────────────────────────────────────
const CartTable = (
    <div style={{flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden'}}>
      <div style={{flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch'}}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-100 text-gray-600">
            <tr>
              <th className="px-2 py-2 text-start w-8"></th>
              {!isMobile && <th className="px-2 py-2 text-end">{showLBP?`${secCode} Total`:t('pos.total','Total')}</th>}
              <th className="px-2 py-2 text-end">{showLBP?`${secCode} Price`:t('pos.price','Price')}</th>
              <th className="px-2 py-2 text-center w-14">{t('pos.qty','Qty')}</th>
              <th className="px-2 py-2 text-end">{t('pos.name','Name')}</th>
            </tr>
          </thead>
          <tbody>
            {cart.map(r=>{
              const selected=r.key===selectedKey;
              return (
                <tr key={r.key} onClick={()=>setSelectedKey(r.key)} className={`border-b cursor-pointer ${selected?'bg-emerald-50':'hover:bg-gray-50'}`}>
                  <td className="px-2 py-2"><button onClick={e=>{e.stopPropagation();removeRow(r.key);}} className="text-red-500 font-bold">✕</button></td>
                  {!isMobile&&<td className="px-2 py-2 text-end font-semibold">{showLBP?`${secCode} ${lbpFmt(r.priceLBP*r.qty)}`:`$ ${money(r.priceUSD*r.qty)}`}</td>}
                  <td className="px-2 py-2 text-end" onClick={e=>e.stopPropagation()}>
                    <input type="number" min={0} step="0.01" value={showLBP?Math.round(r.priceLBP):r.priceUSD}
                      onChange={e=>{const val=Number(e.target.value)||0;if(showLBP)setRowPrice(r.key,val/rate);else setRowPrice(r.key,val);}}
                      className="border rounded px-1 py-0.5 text-end text-sm" style={{width:isMobile?60:96,fontFamily:'inherit'}} />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="number"
                      min={r.scaled?0.001:1}
                      step={r.scaled?0.001:1}
                      value={r.qty}
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>{const v=r.scaled?(parseFloat(e.target.value)||0):(parseInt(e.target.value,10)||1);setQty(r.key,v);}}
                      className="border rounded px-1 py-0.5 text-center"
                      style={{width:r.scaled?64:48}}
                    />
                    {r.scaled&&<div className="text-[10px] text-gray-400">{r.unit||'kg'}</div>}
                  </td>
                  <td className="px-2 py-2 text-end font-bold" style={{fontSize:isMobile?12:13,maxWidth:isMobile?90:200}}>
                    <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.scaled?'⚖️ ':''}{r.name}</div>
                    {(r.modifiers&&r.modifiers.length)?<div style={{fontSize:10,fontWeight:400,color:'#6b7280'}}>{r.modifiers.map(m=>m.name).join(', ')}</div>:null}
                    {r.note?<div style={{fontSize:10,fontWeight:400,color:'#b45309'}}>★ {r.note}</div>:null}
                  </td>
                </tr>
              );
            })}
            {deliveryFeeUSD>0&&(
              <tr className="border-b bg-blue-50">
                <td className="px-2 py-2"></td>
                {!isMobile&&<td className="px-2 py-2 text-end font-semibold text-blue-700">{showLBP?`${secCode} ${lbpFmt(Math.round(deliveryFeeUSD*rate))}`:` $ ${money(deliveryFeeUSD)}`}</td>}
                <td className="px-2 py-2 text-end text-blue-700">{showLBP?lbpFmt(Math.round(deliveryFeeUSD*rate)):money(deliveryFeeUSD)}</td>
                <td className="px-2 py-2 text-center text-blue-700">1</td>
                <td className="px-2 py-2 text-end font-bold text-blue-700" style={{fontSize:isMobile?11:13}}>🚚 Fee</td>
              </tr>
            )}
            {!cart.length&&<tr><td colSpan={isMobile?4:5} className="text-center text-gray-400 py-10 text-sm">{t('pos.empty','Tap a product to add it')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{flexShrink:0}}>
        {(features.discounts!==false||taxFeatureEnabled)&&cart.length>0&&(
          <div className="flex items-center gap-2 px-3 py-1.5 border-t bg-gray-50 text-xs flex-wrap">
            {features.discounts!==false&&(<>
              <span className="text-gray-400">خصم/Disc:</span>
              <button onClick={()=>setDiscountInput(d=>({...d,type:'pct'}))}
                className="px-1.5 py-0.5 rounded border font-bold leading-none"
                style={{background:discountInput.type==='pct'?GREEN:'',color:discountInput.type==='pct'?'#fff':'#555',borderColor:GREEN}}>%</button>
              <button onClick={()=>setDiscountInput(d=>({...d,type:'fixed'}))}
                className="px-1.5 py-0.5 rounded border font-bold leading-none"
                style={{background:discountInput.type==='fixed'?GREEN:'',color:discountInput.type==='fixed'?'#fff':'#555',borderColor:GREEN}}>$</button>
              <input type="number" min={0} max={discountInput.type==='pct'?100:undefined} step={discountInput.type==='pct'?1:0.01}
                placeholder="0" value={discountInput.value}
                onClick={e=>e.stopPropagation()}
                onChange={e=>setDiscountInput(d=>({...d,value:e.target.value}))}
                className="border rounded px-1.5 py-0.5 text-end" style={{width:60}} />
              {discountAmt>0&&<span className="text-red-600 font-bold">−${money(discountAmt)}</span>}
            </>)}
            {taxFeatureEnabled&&defaultTaxRate>0&&(
              <label className="flex items-center gap-1 cursor-pointer ms-auto font-bold select-none"
                style={{color:includeTax?GREEN:'#6b7280'}}>
                <input type="checkbox" checked={includeTax} onChange={e=>setIncludeTax(e.target.checked)}
                  style={{width:14,height:14,accentColor:GREEN}} />
                VAT {defaultTaxRate}%
              </label>
            )}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 font-bold text-sm">
          <span>{itemCount} items · {qtyFmt(qtyCount)} qty</span>
          <div className="text-end">
            {discountAmt>0&&<div className="text-xs text-gray-400 font-normal line-through">${money(totalUSD)}</div>}
            {vatUSD>0&&<div className="text-xs font-normal" style={{color:GREEN}}>VAT {defaultTaxRate}%: ${money(vatUSD)}</div>}
            <span style={{color:GREEN}}>{showLBP?`${secCode} ${lbpFmt(grandTotalLBP)}`:`$ ${money(grandTotalUSD)}`}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // ── MOBILE LAYOUT ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
<div ref={rootRef} className="flex flex-col select-none overflow-hidden" style={{height:areaH, display:'flex', flexDirection:'column', background:'#f0f0f0'}}>

        {/* mobile top bar: total + date + pay mode */}
        <div className="bg-white border-b shrink-0 px-3 py-2 flex items-center gap-3">
          <div className="flex flex-col justify-center px-3 py-1 rounded bg-black text-emerald-400 font-bold text-xs" style={{minWidth:90}}>
            {showLBP&&<span>{lbpFmt(grandTotalLBP)} {secCode}</span>}
            <span>${money(grandTotalUSD)}</span>
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} className="border rounded px-2 py-1 text-xs flex-1" />
              {settingsShowLBP&&(
                <button onClick={toggleCurrency} className="border rounded px-2 py-1 text-xs font-bold whitespace-nowrap" style={{color:GREEN,borderColor:GREEN}}>
                  💱{showLBP?(secCode||'LBP'):'$'}
                </button>
              )}
              <ShiftWidget inline />
              <CalcWidget />
              {features.credit_sales && (
                <>
                  <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap">
                    <input type="radio" checked={payMode==='cash'} onChange={()=>setPayMode('cash')} /> Cash
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap">
                    <input type="radio" checked={payMode==='credit'} onChange={()=>setPayMode('credit')} /> Credit
                  </label>
                </>
              )}
            </div>
            {payMode==='credit'?(
              <select value={accountId} onChange={e=>setAccountId(e.target.value)} className="border rounded px-2 py-1 text-xs w-full">
                <option value="">Select customer</option>
                {customerAccounts.map(a=><option key={CONFIG.f.accId(a)} value={CONFIG.f.accId(a)}>{CONFIG.f.accName(a)}</option>)}
              </select>
            ):(
              <input value={cashCustomer} onChange={e=>setCashCustomer(e.target.value)} placeholder="Cash customer (optional)" className="border rounded px-2 py-1 text-xs w-full" />
            )}
          </div>
        </div>

        {/* mobile tab switcher */}
        <div className="flex bg-white border-b shrink-0">
          <button onClick={()=>setMobileTab('products')}
                  className="flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors"
                  style={{borderColor:mobileTab==='products'?GREEN:'transparent',color:mobileTab==='products'?GREEN:'#6b7280'}}>
            📦 Products
          </button>
          <button onClick={()=>setMobileTab('cart')}
                  className="flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors relative"
                  style={{borderColor:mobileTab==='cart'?GREEN:'transparent',color:mobileTab==='cart'?GREEN:'#6b7280'}}>
            🛒 Cart
            {qtyCount>0&&<span className="ml-1 text-[11px] rounded-full px-1.5 leading-5 font-bold" style={{background:GREEN,color:'#fff'}}>{qtyFmt(qtyCount)}</span>}
          </button>
        </div>

        {/* tab content */}
        <div style={{flex:1, minHeight:0, overflow:'hidden', display:'flex', flexDirection:'column'}}>
          {mobileTab==='products' ? ProductGrid : CartTable}
        </div>

        {/* mobile footer */}
        <div style={{flexShrink:0, background:'#fff', borderTop:'1px solid #e5e7eb', padding:'8px 12px', display:'flex', alignItems:'center', gap:8}}>
          <button onClick={()=>window.history.back()} style={{padding:'8px 12px', borderRadius:8, background:'#f3f4f6', fontWeight:600, fontSize:13, border:'none', cursor:'pointer'}}>✕</button>
          <select value={methodId} onChange={e=>setMethodId(e.target.value)} style={{flex:1, minWidth:0, border:'1.5px solid #e5e7eb', borderRadius:8, padding:'8px 6px', fontSize:13}}>
            {paymentMethods.map(m=><option key={CONFIG.f.pmId(m)} value={CONFIG.f.pmId(m)}>{CONFIG.f.pmName(m)}</option>)}
          </select>
          {features.delivery&&<button disabled={busy||!cart.length} onClick={openDeliveryModal} style={{padding:'8px 12px', borderRadius:8, background:'#f59e0b', color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:'pointer', opacity:busy||!cart.length?0.4:1}}>🚚</button>}
          <button disabled={busy||!cart.length} onClick={()=>startCheckout(false)} style={{padding:'8px 16px', borderRadius:8, background:GREEN, color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:'pointer', opacity:busy||!cart.length?0.4:1}}>Save</button>
          <button disabled={busy||!cart.length} onClick={()=>startCheckout(true)} style={{padding:'8px 16px', borderRadius:8, background:'#059669', color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:'pointer', opacity:busy||!cart.length?0.4:1}}>Print</button>
        </div>

        {/* variant popup */}
        {modFor&&(<ModifierPicker product={modFor} onClose={()=>setModFor(null)} onAdd={(sel)=>{addProductWithMods(modFor,sel);setModFor(null);}} />)}
        {variantModal&&(
          <Modal onClose={()=>setVariantModal(null)} title={`${t('pos.chooseVariant','Choose variant')} — ${CONFIG.f.prodName(variantModal.product)}`}>
            <div className="grid grid-cols-2 gap-3">
              {variantModal.variants.map(v=>(
                <button key={CONFIG.f.varId(v)} onClick={()=>pickVariant(v)} className="p-3 rounded-lg border text-start hover:shadow" style={{borderColor:GREEN}}>
                  <div className="font-bold">{CONFIG.f.varName(v)}</div>
                  <div className="text-sm text-gray-500">{showLBP?`${secCode} ${lbpFmt(CONFIG.f.varPriceLBP(v)||CONFIG.f.prodPriceLBP(variantModal.product)||(CONFIG.f.varPriceUSD(v)||CONFIG.f.prodPriceUSD(variantModal.product))*rate)}`:`$ ${money(CONFIG.f.varPriceUSD(v)||CONFIG.f.prodPriceUSD(variantModal.product))}`}{' · '}{t('pos.stock','stock')}: {CONFIG.f.varStock(v)}</div>
                </button>
              ))}
            </div>
          </Modal>
        )}

        {/* delivery modal */}
        {deliveryModal&&(
          <DeliveryModal
            delivery={delivery} setDelivery={setDelivery} drivers={drivers}
            totalUSD={totalUSD} deliveryFeeUSD={deliveryFeeUSD} grandTotalUSD={grandTotalUSD}
            busy={busy} onClose={()=>setDeliveryModal(null)} onConfirm={confirmDelivery}
          />
        )}

        {/* scan-to-create */}
        {quickCreate&&(
          <QuickCreateModal barcode={quickCreate.barcode} categories={categories} rate={rate}
            onClose={()=>setQuickCreate(null)} onCreated={handleQuickCreated} />
        )}

        {/* payment popup */}
        {payModal&&(<PaymentPopup totalUSD={payModal.isDelivery?grandTotalUSD:finalTotalUSD} rate={rate} showLBP={showLBP} secCode={secCode} secondCurrency={features.second_currency} methods={paymentMethods} methodId={methodId} setMethodId={setMethodId} busy={busy} onCancel={()=>setPayModal(null)} onConfirm={pay=>doSale({print:payModal.print,paidUSD:pay.paidUSD,payAmount:pay.payAmount,payCurrency:pay.payCurrency,deliveryData:payModal.isDelivery?delivery:null})} t={t} />)}
      </div>
    );
  }

  // ── DESKTOP LAYOUT ────────────────────────────────────────────────────────
  return (
    <div ref={rootRef} dir="rtl"
         className="flex flex-col bg-[#f0f0f0] text-gray-800 select-none overflow-hidden"
         style={{height:areaH}}>

      {/* main area: order panel on the right, products fill height on the left */}
      <div className="flex flex-1 min-h-0">

      {/* RIGHT order panel: tabs, date/customer, barcode, then the cart */}
      <div className="flex flex-col bg-white border-l border-gray-200 shrink-0 min-h-0" style={{width:480}}>

      {/* bill tabs */}
      <div className="flex items-center gap-1 px-2 pt-1 bg-white border-b shrink-0 overflow-x-auto">
        {tabs.map((tb,i)=>{
          const label=(tb.cashCustomer&&tb.cashCustomer.trim())||`${t('pos.bill','Bill')} ${i+1}`;
          const count=tb.cart.reduce((s,r)=>s+(r.scaled?1:r.qty),0); const on=tb.id===activeId;
          return (
            <div key={tb.id} onClick={()=>setActiveId(tb.id)}
                 className="flex items-center gap-1.5 px-2 py-1 rounded-t-lg cursor-pointer text-xs whitespace-nowrap"
                 style={{borderBottom:`3px solid ${on?GREEN:'transparent'}`,background:on?'#eafaf6':'transparent',color:on?GREEN:'#444',fontWeight:on?700:500}}>
              <span>{label}</span>
              {count>0&&<span className="text-[11px] rounded-full px-1.5 leading-5 font-bold" style={{background:GREEN,color:'#fff'}}>{qtyFmt(count)}</span>}
              <button onClick={e=>{e.stopPropagation();closeTab(tb.id);}} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
            </div>
          );
        })}
        <button onClick={newTab} className="px-2 py-1 rounded-t-lg text-xs font-bold hover:bg-gray-50" style={{color:GREEN}}>+ {t('pos.newBill','New bill')}</button>
      </div>

      {/* controls — date, customer, barcode, search (top bar + toolbar merged) */}
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-1 bg-white border-b shrink-0 relative">
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        {features.credit_sales && (
          <>
            <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="radio" checked={payMode==='cash'} onChange={()=>setPayMode('cash')} /> {t('pos.cash','Cash')}</label>
            <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="radio" checked={payMode==='credit'} onChange={()=>setPayMode('credit')} /> {t('pos.credit','Credit')}</label>
            <select disabled={payMode!=='credit'} value={accountId} onChange={e=>setAccountId(e.target.value)} className="border rounded px-1 py-1 text-xs min-w-[120px] disabled:bg-gray-100">
              <option value="">{t('pos.selectCustomer','Customer')}</option>
              {customerAccounts.map(a=><option key={CONFIG.f.accId(a)} value={CONFIG.f.accId(a)}>{CONFIG.f.accName(a)}</option>)}
            </select>
          </>
        )}
        <input disabled={payMode!=='cash'} value={cashCustomer} onChange={e=>setCashCustomer(e.target.value)} placeholder={t('pos.cashCustomer','Cash customer')} className="border rounded px-2 py-1 text-xs disabled:bg-gray-100 w-28" />
        <div className="relative">
          <input ref={barcodeRef} onKeyDown={onBarcodeKey} placeholder={t('pos.barcode','Barcode #')}
                 className="border-2 rounded px-2 py-1 text-xs w-28" style={{borderColor:scanMsg?.kind==='error'?'#dc2626':GREEN}} />
          {scanMsg&&(
            <div role="alert" className="absolute z-50 top-full mt-1 start-0 px-3 py-2 rounded-lg shadow-lg text-sm font-semibold whitespace-nowrap"
                 style={{background:scanMsg.kind==='error'?'#fef2f2':'#ecfdf5',color:scanMsg.kind==='error'?'#b91c1c':'#065f46',border:`1.5px solid ${scanMsg.kind==='error'?'#fca5a5':'#6ee7b7'}`}}>
              {scanMsg.kind==='error'?'⚠ ':'✓ '}{scanMsg.text}
            </div>
          )}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('pos.search','Search…')} className="border rounded px-2 py-1 text-xs flex-1 min-w-[90px]" />
        {settingsShowLBP&&(
          <button onClick={toggleCurrency} title={t('pos.switchCurrency','Switch display currency')}
            className="px-2 py-1 rounded border font-bold text-xs hover:bg-gray-100" style={{color:GREEN,borderColor:GREEN}}>
            💱 {showLBP?(secCode||'LBP'):'$'}
          </button>
        )}
        <ShiftWidget inline />
        <CalcWidget />
        <button onClick={toggleFullscreen} title={t('pos.fullscreen','Fullscreen')} className="px-2 py-1 rounded border font-bold text-xs hover:bg-gray-100">⛶</button>
      </div>

      {/* main */}
      <div className="flex flex-1 min-h-0">
        {/* qty keypad */}
        <div className="flex flex-col gap-1.5 p-2 shrink-0" style={{width:64}}>
          {Array.from({length:9},(_,i)=>i+1).map(n=>(
            <button key={n} onClick={()=>applyKeypad(n)} className="w-full flex-1 min-h-[34px] rounded text-white font-bold text-lg hover:opacity-90" style={{backgroundColor:GREEN}}>{n}</button>
          ))}
        </div>
        {/* cart fills the rest of the panel beside the keypad */}
        <div className="flex flex-col min-w-0 bg-white flex-1">
          {CartTable}
        </div>
      </div>{/* end keypad + cart */}
      </div>{/* end right order panel */}

      {/* LEFT: products fill the full height */}
      {ProductGrid}
      </div>{/* end main area */}

      {/* footer */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-t shrink-0" style={{minHeight:68}}>
        <button onClick={()=>window.history.back()} className="flex items-center gap-2 px-6 py-3 rounded bg-gray-100 hover:bg-gray-200 font-semibold text-base">✕ {t('pos.exit','Exit')}</button>
        {/* big black total (moved here so the cart gets the full height) */}
        <div className="flex items-center gap-3 px-5 py-2 rounded bg-black">
          {showLBP&&<span className="text-emerald-400 font-extrabold text-xl leading-none">{lbpFmt(totalLBP)} <span className="text-sm font-bold">{secCode}</span></span>}
          <span className="text-emerald-400 font-extrabold text-3xl leading-none">{money(totalUSD)} <span className="text-base font-bold">$</span></span>
        </div>
        <div className="hidden md:flex flex-col leading-tight px-3 text-sm text-gray-600 font-semibold">
          <span>{t('pos.itemCount','Items')}: {itemCount}</span><span>{t('pos.qty','Qty')}: {qtyFmt(qtyCount)}</span>
        </div>
        <div className="flex items-center gap-2 ms-auto">
          <label className="text-sm">{t('pos.method','Payment method')}</label>
          <select value={methodId} onChange={e=>setMethodId(e.target.value)} className="border rounded px-2 py-2 text-sm min-w-[140px]">
            {paymentMethods.map(m=><option key={CONFIG.f.pmId(m)} value={CONFIG.f.pmId(m)}>{CONFIG.f.pmName(m)}</option>)}
          </select>
        </div>
        {features.delivery&&(<button disabled={busy||!cart.length} onClick={openDeliveryModal} className="px-5 py-3 rounded font-bold text-base disabled:opacity-40 text-white" style={{backgroundColor:'#f59e0b'}}>🚚 {t('pos.delivery','Delivery')}</button>)}
        <button disabled={busy||!cart.length} onClick={()=>startCheckout(true)} className="px-7 py-3 rounded text-white font-bold text-base disabled:opacity-40" style={{backgroundColor:GREEN}}>{t('pos.savePrint','Save & Print')} <span className="opacity-70 text-xs">(F3)</span></button>
        <button disabled={busy||!cart.length} onClick={()=>startCheckout(false)} className="px-7 py-3 rounded text-white font-bold text-base disabled:opacity-40 bg-emerald-600">{t('pos.save','Save')} <span className="opacity-70 text-xs">(F4)</span></button>
      </div>

      {/* variant popup */}
      {modFor&&(<ModifierPicker product={modFor} onClose={()=>setModFor(null)} onAdd={(sel)=>{addProductWithMods(modFor,sel);setModFor(null);}} />)}
      {variantModal&&(
        <Modal onClose={()=>setVariantModal(null)} title={`${t('pos.chooseVariant','Choose variant')} — ${CONFIG.f.prodName(variantModal.product)}`}>
          <div className="grid grid-cols-2 gap-3">
            {variantModal.variants.map(v=>(
              <button key={CONFIG.f.varId(v)} onClick={()=>pickVariant(v)} className="p-3 rounded-lg border text-start hover:shadow" style={{borderColor:GREEN}}>
                <div className="font-bold">{CONFIG.f.varName(v)}</div>
                <div className="text-sm text-gray-500">{showLBP?`${secCode} ${lbpFmt(CONFIG.f.varPriceLBP(v)||CONFIG.f.prodPriceLBP(variantModal.product)||(CONFIG.f.varPriceUSD(v)||CONFIG.f.prodPriceUSD(variantModal.product))*rate)}`:`$ ${money(CONFIG.f.varPriceUSD(v)||CONFIG.f.prodPriceUSD(variantModal.product))}`}{' · '}{t('pos.stock','stock')}: {CONFIG.f.varStock(v)}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* delivery modal */}
      {deliveryModal&&(
        <DeliveryModal
          delivery={delivery} setDelivery={setDelivery} drivers={drivers}
          totalUSD={totalUSD} deliveryFeeUSD={deliveryFeeUSD} grandTotalUSD={grandTotalUSD}
          busy={busy} onClose={()=>setDeliveryModal(null)} onConfirm={confirmDelivery}
        />
      )}

      {/* scan-to-create */}
      {quickCreate&&(
        <QuickCreateModal barcode={quickCreate.barcode} categories={categories} rate={rate}
          onClose={()=>{setQuickCreate(null);barcodeRef.current?.focus();}} onCreated={handleQuickCreated} />
      )}

      {/* payment popup */}
      {payModal&&(<PaymentPopup totalUSD={payModal.isDelivery?grandTotalUSD:finalTotalUSD} rate={rate} showLBP={showLBP} secCode={secCode} secondCurrency={features.second_currency} methods={paymentMethods} methodId={methodId} setMethodId={setMethodId} busy={busy} onCancel={()=>setPayModal(null)} onConfirm={pay=>doSale({print:payModal.print,paidUSD:pay.paidUSD,payAmount:pay.payAmount,payCurrency:pay.payCurrency,deliveryData:payModal.isDelivery?delivery:null})} t={t} />)}
    </div>
  );
}

// scan-to-create: unknown barcode → create the product without leaving the sale
function QuickCreateModal({barcode,categories,rate,onClose,onCreated}){
  const [name,setName]=useState('');
  const [price,setPrice]=useState('');
  const [cost,setCost]=useState('');
  const [catId,setCatId]=useState('');
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const save=async()=>{
    if(!name.trim()||price===''||Number(price)<0){setErr('Name and price are required');return;}
    setSaving(true);setErr('');
    try{
      const res=await api.post('/api/products',{
        name:name.trim(), type:'simple', barcode,
        category_id:catId||null,
        price_usd:Number(price), price_lbp:Math.round(Number(price)*(rate||89500)),
        cost:Number(cost)||0, cost_currency:'USD',
        stock:0, manage_stock:true, unit:'piece',
      });
      onCreated(res.data?.data??res.data);
    }catch(e){setErr(e.response?.data?.error||'Could not create the product');setSaving(false);}
  };
  return(
    <Modal onClose={onClose} title="🆕 New product — not in the system">
      <div className="space-y-3">
        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 font-mono">Barcode: <b>{barcode}</b></div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Product name *</label>
          <input autoFocus value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')save();}}
            placeholder="e.g. Pepsi 330ml" className="w-full border rounded px-3 py-2 text-sm" style={{borderColor:GREEN}} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sale price (USD) *</label>
            <input type="number" min="0" step="0.01" value={price} onChange={e=>setPrice(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')save();}}
              placeholder="0.00" className="w-full border rounded px-3 py-2 text-sm" style={{borderColor:GREEN}} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Cost (USD) — optional</label>
            <input type="number" min="0" step="0.01" value={cost} onChange={e=>setCost(e.target.value)}
              placeholder="0.00" className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Category — optional</label>
          <select value={catId} onChange={e=>setCatId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">— None —</option>
            {categories.map(c=><option key={c._id??c.id} value={c._id??c.id}>{c.name}</option>)}
          </select>
        </div>
        {err&&<div className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{err}</div>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-gray-100 hover:bg-gray-200 font-semibold">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 rounded text-white font-bold disabled:opacity-40" style={{backgroundColor:GREEN}}>
            {saving?'Saving…':'Create & add to cart'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DeliveryModal({delivery,setDelivery,drivers,totalUSD,deliveryFeeUSD,grandTotalUSD,busy,onClose,onConfirm}){
  return(
    <Modal onClose={onClose} title="🚚 Delivery Order">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs text-gray-500 block mb-1">Delivery Address *</label><input autoFocus value={delivery.address} onChange={e=>setDelivery(d=>({...d,address:e.target.value}))} placeholder="Street, building, floor…" className="w-full border rounded px-3 py-2 text-sm" /></div>
          <div><label className="text-xs text-gray-500 block mb-1">Phone</label><input value={delivery.phone} onChange={e=>setDelivery(d=>({...d,phone:e.target.value}))} placeholder="+961 xx xxx xxx" className="w-full border rounded px-3 py-2 text-sm" /></div>
          <div><label className="text-xs text-gray-500 block mb-1">Delivery Fee (USD)</label><input type="number" min="0" step="0.5" value={delivery.fee} onChange={e=>setDelivery(d=>({...d,fee:e.target.value}))} placeholder="0.00" className="w-full border rounded px-3 py-2 text-sm" /></div>
          {drivers.length>0&&(<div className="col-span-2"><label className="text-xs text-gray-500 block mb-1">Assign Driver</label><select value={delivery.driverUserId} onChange={e=>setDelivery(d=>({...d,driverUserId:e.target.value}))} className="w-full border rounded px-3 py-2 text-sm"><option value="">— No driver assigned —</option>{drivers.map(u=><option key={u._id} value={u._id}>{u.name}</option>)}</select></div>)}
          <div className="col-span-2"><label className="text-xs text-gray-500 block mb-1">Notes</label><input value={delivery.notes} onChange={e=>setDelivery(d=>({...d,notes:e.target.value}))} placeholder="Ring bell, call before arriving…" className="w-full border rounded px-3 py-2 text-sm" /></div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-gray-500">Items total</span><span className="font-semibold">${money(totalUSD)}</span></div>
          {deliveryFeeUSD>0&&<div className="flex justify-between"><span className="text-gray-500">Delivery fee</span><span className="font-semibold">${money(deliveryFeeUSD)}</span></div>}
          <div className="flex justify-between border-t pt-1 mt-1"><span className="font-bold">Grand total</span><span className="font-bold text-base">${money(grandTotalUSD)}</span></div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 text-sm" style={{borderColor:delivery.paidNow?'#2e9f86':'#e5e7eb',background:delivery.paidNow?'#f0fdf8':'#fff'}}>
          <input type="checkbox" checked={delivery.paidNow} onChange={e=>setDelivery(d=>({...d,paidNow:e.target.checked}))} />
          <span className="font-semibold">💵 Paid in advance</span>
          <span className="text-xs text-gray-500">— leave unchecked if the driver collects cash on delivery</span>
        </label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-gray-100 hover:bg-gray-200 font-semibold">Cancel</button>
          <button onClick={()=>onConfirm(true)} disabled={busy} className="flex-1 py-2 rounded text-white font-bold disabled:opacity-40" style={{backgroundColor:GREEN}}>💬 Confirm & Print</button>
          <button onClick={()=>onConfirm(false)} disabled={busy} className="flex-1 py-2 rounded text-white font-bold disabled:opacity-40" style={{backgroundColor:'#f59e0b'}}>🚚 Confirm</button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({title,children,onClose}){
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[520px] max-w-[92vw] p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg">{title}</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button></div>
        {children}
      </div>
    </div>
  );
}

function PaymentPopup({totalUSD,rate,showLBP,secCode='LBP',secondCurrency,methods,methodId,setMethodId,busy,onCancel,onConfirm,t}){
  const [payCur,setPayCur]=useState('USD');
  const [paid,setPaid]=useState(totalUSD.toFixed(2));
  const switchCur=(c)=>{setPayCur(c);setPaid(c==='USD'?totalUSD.toFixed(2):String(Math.round(totalUSD*rate)));};
  const payAmount=Number(paid)||0;
  const paidUSD=payCur==='USD'?payAmount:payAmount/rate;   // always normalise to USD for the doc balance
  const remaining=totalUSD-paidUSD;
  const fId=(m)=>m._id??m.id; const fName=(m)=>m.name??m.Name;
  const tabBtn=(active)=>`flex-1 py-2 rounded font-bold text-sm ${active?'text-white':'bg-gray-100 text-gray-700'}`;
  const dual=(usd)=>`$ ${money(usd)}${secondCurrency?`  ·  ${secCode} ${lbpFmt(usd*rate)}`:''}`;
  return(
    <Modal title={t('pos.payment','Payment')} onClose={onCancel}>
      <div className="space-y-3">
        <Row label={t('pos.total','Total')} value={dual(totalUSD)} bold />
        {secondCurrency&&(
          <div className="flex gap-2">
            <button onClick={()=>switchCur('USD')} className={tabBtn(payCur==='USD')} style={payCur==='USD'?{backgroundColor:GREEN}:{}}>USD</button>
            <button onClick={()=>switchCur(secCode)} className={tabBtn(payCur!=='USD')} style={payCur!=='USD'?{backgroundColor:GREEN}:{}}>{secCode}</button>
          </div>
        )}
        <div><label className="text-sm text-gray-600">{payCur==='USD'?t('pos.paidUSD','Amount paid (USD)'):`${t('pos.paid','Amount paid')} (${secCode})`}</label><input type="number" step="0.01" value={paid} autoFocus onChange={e=>setPaid(e.target.value)} className="w-full border rounded px-3 py-2 mt-1 text-lg" /></div>
        <div><label className="text-sm text-gray-600">{t('pos.method','Payment method')}</label><select value={methodId} onChange={e=>setMethodId(e.target.value)} className="w-full border rounded px-3 py-2 mt-1">{methods.map(m=><option key={fId(m)} value={fId(m)}>{fName(m)}</option>)}</select></div>
        <Row label={t('pos.remaining','Remaining (debt)')} value={dual(remaining)} className={remaining>0.01?'text-red-600':'text-emerald-600'} bold />
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="flex-1 py-2 rounded bg-gray-100 hover:bg-gray-200 font-semibold">{t('common.cancel','Cancel')}</button>
          <button disabled={busy||paidUSD<0} onClick={()=>onConfirm({paidUSD,payAmount,payCurrency:payCur})} className="flex-1 py-2 rounded text-white font-bold disabled:opacity-40" style={{backgroundColor:GREEN}}>{t('pos.confirm','Confirm sale')}</button>
        </div>
      </div>
    </Modal>
  );
}

function Row({label,value,bold,className=''}){
  return(<div className="flex items-center justify-between"><span className="text-gray-600">{label}</span><span className={`${bold?'font-bold text-lg':''} ${className}`}>{value}</span></div>);
}

function asArray(data){
  if(Array.isArray(data))return data;
  if(Array.isArray(data?.data))return data.data;
  if(Array.isArray(data?.items))return data.items;
  if(Array.isArray(data?.results))return data.results;
  return[];
}